import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useJurisdictions, Requirements } from "@/components/Jurisdictions";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Circle, Compass, ChevronDown, ChevronUp } from "lucide-react";

const STEPS = [
  ["hosting", "Turn on hosting and accept the Host Agreement", null],
  ["stripe", "Set up payouts with Stripe", "/host/payouts", "About 10 minutes with your bank account and ID. Guests can't book your home until this is done."],
  ["listing", "Create your first listing", "/host/listings/new"],
  ["jurisdiction", "Add the home's physical address so its tax zone is verified", "edit", "We check it against Clark County's official room tax map."],
  ["license", "Add your short-term rental license and its expiry date", "edit"],
  ["permit", "Add your room tax permit and its expiry date", "edit"],
  ["insurance", "Upload your short-term rental insurance", "edit"],
  ["approved", "Get approved by the FairStay team", null, "We check your permit, license and insurance, usually within 2 business days."],
];

const STRIPE_HINT = {
  verifying: "Your details are in. Stripe is still verifying them, which can take a day or two.",
  in_progress: "Stripe still needs a few details from you. Pick up where you left off.",
  unknown: "We couldn't reach Stripe just now to check your setup. Try again in a few minutes.",
};

function Step({ s, done, href, hint: override }) {
  const [key, title, , base] = s;
  const hint = override || base;
  const Icon = done ? CheckCircle2 : Circle;
  return (
    <li className="flex items-start gap-3 rounded-2xl border bg-white p-4" data-testid={`guide-step-${key}`}>
      <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${done ? "text-emerald-600" : "text-stone-300"}`} data-testid={`guide-step-${key}-${done ? "done" : "todo"}`} />
      <div className="flex-1">
        <p className={`text-sm font-medium ${done ? "text-stone-500 line-through" : ""}`}>{title}</p>
        {hint && <p className="text-xs text-stone-500">{hint}</p>}
      </div>
      {!done && href && <Button asChild size="sm" variant="outline" className="rounded-full" data-testid={`guide-step-${key}-go`}><Link to={href}>Go</Link></Button>}
    </li>
  );
}

// Welcome checklist for new owners: payouts, licenses, permits, insurance and the first listing.
export function WelcomeGuide() {
  const [open, setOpen] = useState(true);
  const [area, setArea] = useState(null);
  const { data } = useQuery({ queryKey: ["host-onboarding"], queryFn: () => api.get("/host/onboarding").then((r) => r.data) });
  const { data: list = [] } = useJurisdictions();
  if (!data || data.done === data.total) return null;
  const pickedArea = area ?? data.jurisdiction;
  const linkFor = (target) => (target === "edit" ? (data.listing_id ? `/host/listings/${data.listing_id}/edit` : "/host/listings/new") : target);
  return (
    <section className="mb-10 rounded-3xl border border-teal-200 bg-teal-50/40 p-6" data-testid="welcome-guide">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2"><Compass className="h-5 w-5 text-primary" /><h2 className="text-lg font-semibold">Welcome to FairStay: your setup checklist</h2></div>
        <Button size="sm" variant="ghost" className="rounded-full" onClick={() => setOpen(!open)} data-testid="welcome-guide-toggle">
          {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}{open ? "Hide" : "Show"}
        </Button>
      </div>
      <div className="mt-3 flex items-center gap-3"><Progress value={(data.done / data.total) * 100} className="h-2" /><span className="whitespace-nowrap text-sm font-semibold" data-testid="welcome-guide-progress">{data.done} of {data.total} done</span></div>
      {open && (
        <div className="mt-5 grid gap-6 lg:grid-cols-2">
          <ol className="space-y-2">{STEPS.map((s) => <Step key={s[0]} s={s} done={data.steps[s[0]]} href={s[2] && linkFor(s[2])} hint={s[0] === "stripe" && !data.steps.stripe ? STRIPE_HINT[data.stripe_state] : null} />)}</ol>
          <div className="space-y-3">
            <p className="text-sm font-semibold">License and tax rules for your area</p>
            <select className="w-full rounded-xl border bg-white px-3 py-2 text-sm" value={pickedArea || ""} onChange={(e) => setArea(e.target.value)} data-testid="welcome-guide-area-select">
              <option value="">Choose your Nevada area</option>
              {list.map((j) => <option key={j.key} value={j.key}>{j.name}</option>)}
            </select>
            <Requirements j={list.find((j) => j.key === pickedArea)} testid="welcome-guide-requirements" />
          </div>
        </div>
      )}
    </section>
  );
}
